import React, { useEffect, useState } from "react";
import { getUsers, createUser, activateUser, updateUser, updatePremium, bulkPremiumUpdate, getUserInfo, forgotPassword, getCities, getDistricts } from "../../api/users";
import toast from "react-hot-toast";
import { Icon } from "@iconify/react";
import api from "../../api/axios";
import { useLoader } from "../../context/LoaderContext";
import Pagination from "../../components/Pagination";

const educationLevelMap = {
  "İlkokul": 1,
  "Ilkokul": 1,
  "Ortaokul": 2,
  "Lise": 3,
  "Ön Lisans": 4,
  "Lisans": 5,
  "Yüksek Lisans": 6,
  "Doktora": 7
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const { showLoader, hideLoader } = useLoader();
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  // Bulk Selection
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  // Premium Modal
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [isBulkPremium, setIsBulkPremium] = useState(false);
  const [premiumData, setPremiumData] = useState({
    id: null,
    isPremium: false,
    premiumExpireDate: new Date().toISOString().split('T')[0],
    userIds: []
  });

  const [formData, setFormData] = useState({
    userName: "",
    name: "",
    surname: "",
    email: "",
    city: "",
    district: "",
    educationLevel: 1,
    grade: 0,
    birthYear: 0,
    isPremium: false,
    premiumExpireDate: "",
    isActive: true,
    oneSignalId: "",
    language: "tr"
  });

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordChangingUserId, setPasswordChangingUserId] = useState(null);
  const [passwordFormData, setPasswordFormData] = useState({
    current_password: "",
    new_password: "",
    new_password_confirmation: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [showResetSuccess, setShowResetSuccess] = useState(false);
  const [resetSuccessData, setResetSuccessData] = useState({
    newPassword: "",
    notice: "",
    email: ""
  });
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [userProfileLoading, setUserProfileLoading] = useState(false);

  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);

  // === USERS LOAD ===
  const loadUsers = async () => {
    try {
      showLoader();
      const res = await getUsers();
      if (res.isTrue) {
        setAllUsers(res.data || []);
      } else {
        toast.error(res.message || "Kullanıcılar alınamadı!");
      }
    } catch (err) {
      toast.error(err.message || "Kullanıcılar alınamadı!");
    } finally {
      hideLoader();
    }
  };

  const loadCities = async () => {
    try {
      const res = await getCities();
      if (res.isTrue) {
        setCities(res.data);
      }
    } catch (err) {
      console.error("Cities load error:", err);
    }
  };

  const loadDistricts = async (cityId) => {
    if (!cityId) {
      setDistricts([]);
      return;
    }
    try {
      const res = await getDistricts(cityId);
      if (res.isTrue) {
        setDistricts(res.data);
      }
    } catch (err) {
      console.error("Districts load error:", err);
    }
  };

  // === FILTER VE PAGINATE ===
  useEffect(() => {
    let filtered = allUsers;

    // Search filtresi
    if (searchTerm) {
      filtered = filtered.filter((user) =>
        (user.name + " " + user.surname)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filtresi
    if (statusFilter) {
      const statusValue = statusFilter === "Aktif";
      filtered = filtered.filter((user) => user.isActive === statusValue);
    }

    // ID'ye göre sıralama (küçükten büyüğe)
    filtered = [...filtered].sort((a, b) => a.id - b.id);

    setTotalCount(filtered.length);

    // Pagination logic
    const indexOfLastItem = currentPage * pageSize;
    const indexOfFirstItem = indexOfLastItem - pageSize;
    const currentItems = filtered.slice(indexOfFirstItem, indexOfLastItem);

    setUsers(currentItems);
  }, [allUsers, searchTerm, statusFilter, pageSize, currentPage]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, pageSize]);


  useEffect(() => {
    loadUsers();
    loadCities();
  }, []);


  // === TOGGLE USER STATUS ===
  const toggleUserStatus = async (user) => {
    try {
      showLoader();
      const res = await activateUser(user.id, !user.isActive);

      if (res.isTrue) {
        toast.success(res.message || "Kullanıcı durumu güncellendi!");
        await loadUsers();
      } else {
        toast.error(res.message || "Durum güncellenemedi!");
      }
    } catch (err) {
      toast.error(err.message || "Durum güncelleme hatası!");
    } finally {
      hideLoader();
    }
  };

  // === MODAL ===
  const openModal = () => {
    setEditingUserId(null);
    setFormData({
      userName: "",
      name: "",
      surname: "",
      email: "",
      city: "",
      district: "",
      educationLevel: 1,
      grade: 0,
      birthYear: 0,
      isPremium: false,
      premiumExpireDate: "",
      isActive: true,
      oneSignalId: "",
      language: "tr"
    });
    setDistricts([]);
    setShowModal(true);
  };

  const openEditModal = async (user) => {
    try {
      showLoader();
      let userData = user;

      const res = await getUserInfo(user.id);
      if (res.isTrue && res.data) {
        userData = res.data;
      }

      const cityId = userData.cityId || userData.city;
      if (cityId) {
        await loadDistricts(cityId);
      } else {
        setDistricts([]);
      }

      // Map string education level to its numeric value
      let eduLevel = userData.educationLevel;
      if (typeof eduLevel === 'string') {
        eduLevel = educationLevelMap[eduLevel] || eduLevel;
      }

      setEditingUserId(userData.id);
      setFormData({
        userName: userData.userName || "",
        name: userData.name || "",
        surname: userData.surname || "",
        email: userData.email || "",
        city: cityId || "",
        district: userData.districtId || userData.district || "",
        educationLevel: eduLevel || 1,
        grade: userData.grade || 0,
        birthYear: userData.birthYear || 0,
        isPremium: userData.isPremium || false,
        premiumExpireDate: userData.premiumExpireDate ? userData.premiumExpireDate.split('T')[0] : "",
        isActive: userData.isActive == 1 || userData.isActive === true,
        oneSignalId: userData.oneSignalId || "",
        language: userData.language || "tr"
      });
      setShowModal(true);
    } catch (err) {
      console.error("User info fetch error for edit:", err);
      toast.error("Kullanıcı detayları alınamadı, mevcut bilgilerle devam ediliyor.");

      // Fallback logic
      setEditingUserId(user.id);
      setFormData({
        userName: user.userName || "",
        name: user.name || "",
        surname: user.surname || "",
        email: user.email || "",
        city: user.city || "",
        district: user.district || "",
        educationLevel: user.educationLevel || 1,
        grade: user.grade || 0,
        birthYear: user.birthYear || 0,
        isPremium: user.isPremium || false,
        premiumExpireDate: user.premiumExpireDate ? user.premiumExpireDate.split('T')[0] : "",
        isActive: user.isActive ?? true,
        oneSignalId: user.oneSignalId || "",
        language: user.language || "tr"
      });
      setShowModal(true);
    } finally {
      hideLoader();
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUserId(null);
  };

  // === PASSWORD CHANGE MODAL ===
  const openPasswordModal = (userId) => {
    if (userId === 1) {
      toast.error("Sistem hesabı şifresi değiştirilemez!");
      return;
    }

    setPasswordChangingUserId(userId);
    setPasswordFormData({
      current_password: "",
      new_password: "",
      new_password_confirmation: ""
    });
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordChangingUserId(null);
    setPasswordFormData({
      current_password: "",
      new_password: "",
      new_password_confirmation: ""
    });
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!passwordFormData.current_password || !passwordFormData.new_password || !passwordFormData.new_password_confirmation) {
      toast.error("Lütfen tüm alanları doldurun!");
      return;
    }

    if (passwordFormData.new_password !== passwordFormData.new_password_confirmation) {
      toast.error("Yeni şifreler eşleşmiyor!");
      return;
    }

    if (passwordFormData.new_password.length < 6) {
      toast.error("Şifre en az 6 karakter olmalı!");
      return;
    }

    try {
      showLoader();
      await api.post(`api/v1/users/password/${passwordChangingUserId}`, {
        current_password: passwordFormData.current_password,
        new_password: passwordFormData.new_password,
        new_password_confirmation: passwordFormData.new_password_confirmation
      });

      toast.success("Şifre başarıyla güncellendi!");
      closePasswordModal();
    } catch (err) {
      toast.error(err.message || "Şifre güncellenemedi!");
    } finally {
      hideLoader();
    }
  };

  const handleForgotPasswordRequest = async (email) => {
    if (!email) {
      toast.error("Kullanıcı e-posta adresi bulunamadı!");
      return;
    }

    if (!window.confirm(`${email} adresine şifre sıfırlama e-postası göndermek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      showLoader();
      const res = await forgotPassword(email);
      toast.success(res.message || "Şifre sıfırlama e-postası başarıyla gönderildi!");
    } catch (err) {
      toast.error(err.message || "Şifre sıfırlama isteği gönderilemedi!");
    } finally {
      hideLoader();
    }
  };

  const handleResetPassword = async (userId) => {
    if (userId === 1) {
      toast.error("Sistem hesabı şifresi sıfırlanamaz!");
      return;
    }

    setResetUserId(userId);
    setShowResetConfirm(true);
  };

  const confirmResetPassword = async () => {
    try {
      showLoader();
      const response = await api.post(`api/v1/users/password/reset/${resetUserId}`);

      if (response.data?.success) {
        const newPassword = response.data.data.new_password;
        const notice = response.data.data.notice;
        const user = allUsers.find(u => u.id === resetUserId);

        setResetSuccessData({
          newPassword: newPassword,
          notice: notice,
          email: user?.email || ""
        });

        setShowResetSuccess(true);
        await loadUsers();
      }
      setShowResetConfirm(false);
    } catch (err) {
      toast.error(err.message || "Şifre sıfırlanamadı!");
      setShowResetConfirm(false);
    } finally {
      hideLoader();
    }
  };

  const cancelResetPassword = () => {
    setShowResetConfirm(false);
    setResetUserId(null);
  };

  const closeResetSuccess = () => {
    setShowResetSuccess(false);
    setResetSuccessData({
      newPassword: "",
      notice: "",
      email: ""
    });
  };

  // === USER PROFILE MODAL ===
  const openUserProfileModal = async (user) => {
    try {
      showLoader();
      const res = await getUserInfo(user.id);
      if (res.isTrue && res.data) {
        setSelectedUserProfile(res.data);
      } else {
        setSelectedUserProfile(user);
      }
    } catch (err) {
      console.error("User info fetch error:", err);
      // Fallback to existing data if fetch fails
      setSelectedUserProfile(user);
    } finally {
      hideLoader();
      setShowUserProfileModal(true);
    }
  };

  const closeUserProfileModal = () => {
    setShowUserProfileModal(false);
    setSelectedUserProfile(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.name || !formData.surname) {
      toast.error("Lütfen zorunlu alanları doldurun!");
      return;
    }

    try {
      showLoader();

      if (editingUserId) {
        const res = await updateUser(editingUserId, formData);
        if (res.isTrue) {
          toast.success(res.message || "Kullanıcı başarıyla güncellendi!");
          await loadUsers();
          closeModal();
        } else {
          toast.error(res.message || "Güncelleme başarısız!");
        }
      } else {
        const res = await createUser(formData);
        if (res.isTrue) {
          toast.success(res.message || "Kullanıcı başarıyla oluşturuldu!");
          await loadUsers();
          closeModal();
        } else {
          toast.error(res.message || "Oluşturma başarısız!");
        }
      }
    } catch (err) {
      toast.error(err.message || "İşlem başarısız!");
    } finally {
      hideLoader();
    }
  };

  // === PREMIUM ACTIONS ===
  const openPremiumModal = (user = null) => {
    if (user) {
      setIsBulkPremium(false);
      setPremiumData({
        id: user.id,
        isPremium: user.isPremium,
        premiumExpireDate: user.premiumExpireDate ? user.premiumExpireDate.split('T')[0] : new Date().toISOString().split('T')[0],
        userIds: []
      });
    } else {
      if (selectedUserIds.length === 0) {
        toast.error("Lütfen en az bir kullanıcı seçin!");
        return;
      }
      setIsBulkPremium(true);
      setPremiumData({
        id: null,
        isPremium: true,
        premiumExpireDate: new Date().toISOString().split('T')[0],
        userIds: selectedUserIds
      });
    }
    setShowPremiumModal(true);
  };

  const handlePremiumSubmit = async (e) => {
    e.preventDefault();
    try {
      showLoader();
      let res;
      if (isBulkPremium) {
        res = await bulkPremiumUpdate({
          userIds: premiumData.userIds,
          expireDate: new Date(premiumData.premiumExpireDate).toISOString(),
          isPremium: premiumData.isPremium
        });
      } else {
        res = await updatePremium({
          userId: premiumData.id,
          isPremium: premiumData.isPremium,
          premiumExpireDate: new Date(premiumData.premiumExpireDate).toISOString()
        });
      }

      if (res.isTrue) {
        toast.success(res.message || "Premium durumu güncellendi!");
        await loadUsers();
        setShowPremiumModal(false);
        setSelectedUserIds([]);
      } else {
        toast.error(res.message || "İşlem başarısız!");
      }
    } catch (err) {
      toast.error(err.message || "Hata oluştu!");
    } finally {
      hideLoader();
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map(u => u.id));
    }
  };

  // === Helper Functions ===

  return (
    <div className="card h-100 p-0 radius-12">

      {/* HEADER */}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">Show</span>

          <select
            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
            value={pageSize}
            onChange={(e) => setPageSize(parseInt(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>

          {/* Search */}
          <form className="navbar-search">
            <input
              type="text"
              className="bg-base h-40-px w-auto"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </form>

          <select
            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Status</option>
            <option value="Aktif">Aktif</option>
            <option value="Pasif">Pasif</option>
          </select>
        </div>

        {/* ADD USER BUTTON */}
        <div className="d-flex align-items-center gap-2">
          {selectedUserIds.length > 0 && (
            <button
              onClick={() => openPremiumModal()}
              className="btn btn-warning text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            >
              <Icon icon="mdi:crown" className="icon text-xl" />
              Toplu Premium ({selectedUserIds.length})
            </button>
          )}
          <button
            onClick={openModal}
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Kullanıcı Ekle
          </button>
        </div>
      </div>

      {/* BODY */}
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th className="text-center">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={selectedUserIds.length === users.length && users.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </div>
                </th>
                <th>#</th>
                <th>Kullanıcı Adı</th>
                <th>Ad Soyad</th>
                <th>Email</th>
                <th className="text-center">Premium</th>
                <th className="text-center">Premium Bitiş</th>
                <th className="text-center">Status</th>
                <th className="text-center">#</th>
              </tr>
            </thead>

            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center py-20">
                    Kullanıcı bulunamadı.
                  </td>
                </tr>
              )}

              {users.map((user) => (
                <tr key={user.id}>
                  <td className="text-center">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                      />
                    </div>
                  </td>
                  <td>{user.id}</td>
                  <td>{user.userName}</td>
                  <td>{user.name} {user.surname}</td>
                  <td>{user.email}</td>
                  <td className="text-center">
                    {user.isPremium ? (
                      <span className="badge bg-warning-focus text-warning-main px-12 py-4 radius-4 text-xs fw-bold">
                        PREMIUM
                      </span>
                    ) : (
                      <span className="badge bg-neutral-200 text-neutral-600 px-12 py-4 radius-4 text-xs">
                        STANDART
                      </span>
                    )}
                  </td>
                  <td className="text-center">
                    {user.premiumExpireDate ? user.premiumExpireDate.substring(0, 10) : "-"}
                  </td>
                  <td className="text-center">
                    <button
                      onClick={() => toggleUserStatus(user)}
                      disabled={loading}
                      style={{
                        border: "none",
                        background: "none",
                        cursor: loading ? "not-allowed" : "pointer",
                        padding: 0,
                      }}
                    >
                      {user.isActive ? (
                        <span className="bg-success-focus text-success-600 border border-success-main px-24 py-4 radius-4 text-sm">
                          Aktif
                        </span>
                      ) : (
                        <span className="bg-neutral-200 text-neutral-600 border border-neutral-400 px-24 py-4 radius-4 text-sm">
                          Pasif
                        </span>
                      )}
                    </button>
                  </td>
                  <td className="text-center">
                    <div className="d-flex align-items-center gap-10 justify-content-center">
                      <button
                        onClick={() => openUserProfileModal(user)}
                        className="bg-info-focus text-info-600 w-32-px h-32-px rounded-circle d-flex justify-content-center align-items-center"
                        title="Profil"
                      >
                        <Icon icon="majesticons:eye-line" />
                      </button>
                      <button
                        onClick={() => openEditModal(user)}
                        className="bg-success-focus text-success-600 w-32-px h-32-px rounded-circle d-flex justify-content-center align-items-center"
                        title="Düzenle"
                      >
                        <Icon icon="lucide:edit" />
                      </button>
                      <button
                        onClick={() => openPremiumModal(user)}
                        className="bg-warning-focus text-warning-600 w-32-px h-32-px rounded-circle d-flex justify-content-center align-items-center"
                        title="Premium"
                      >
                        <Icon icon="mdi:crown" />
                      </button>
                      <button
                        onClick={() => handleForgotPasswordRequest(user.email)}
                        className="bg-danger-focus text-danger-600 w-32-px h-32-px rounded-circle d-flex justify-content-center align-items-center"
                        title="Şifre Sıfırlama E-postası Gönder"
                      >
                        <Icon icon="lucide:key" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalCount={totalCount}
        />
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }} role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingUserId ? "Kullanıcı Güncelle" : "Yeni Kullanıcı Ekle"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-20">
                      <label className="form-label">Kullanıcı Adı *</label>
                      <input
                        type="text"
                        name="userName"
                        className="form-control"
                        value={formData.userName}
                        onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-20">
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-20">
                      <label className="form-label">Ad *</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-20">
                      <label className="form-label">Soyad *</label>
                      <input
                        type="text"
                        name="surname"
                        className="form-control"
                        value={formData.surname}
                        onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-20">
                      <label className="form-label">Şehir</label>
                      <select
                        className="form-select"
                        value={formData.city}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData({ ...formData, city: val, district: "" });
                          loadDistricts(val);
                        }}
                      >
                        <option value="">Şehir Seçiniz</option>
                        {cities.map(city => (
                          <option key={city.id} value={city.id}>{city.ad}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6 mb-20">
                      <label className="form-label">İlçe</label>
                      <select
                        className="form-select"
                        value={formData.district}
                        onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                        disabled={!formData.city}
                      >
                        <option value="">İlçe Seçiniz</option>
                        {districts.map(district => (
                          <option key={district.id} value={district.id}>{district.ad}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4 mb-20">
                      <label className="form-label">Eğitim Seviyesi</label>
                      <select
                        className="form-select"
                        value={formData.educationLevel}
                        onChange={(e) => setFormData({ ...formData, educationLevel: parseInt(e.target.value) })}
                      >
                        <option value="1">İlkokul</option>
                        <option value="2">Ortaokul</option>
                        <option value="3">Lise</option>
                        <option value="4">Ön Lisans</option>
                        <option value="5">Lisans</option>
                        <option value="6">Yüksek Lisans</option>
                        <option value="7">Doktora</option>
                      </select>
                    </div>
                    <div className="col-md-4 mb-20">
                      <label className="form-label">Sınıf</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.grade}
                        onChange={(e) => setFormData({ ...formData, grade: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="col-md-4 mb-20">
                      <label className="form-label">Doğum Yılı</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.birthYear}
                        onChange={(e) => setFormData({ ...formData, birthYear: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="col-md-12 mb-20">
                      <div className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          id="isActiveSwitch"
                        />
                        <label className="form-check-label" htmlFor="isActiveSwitch">Kullanıcı Aktif mi?</label>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer px-0 pb-0">
                    <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={loading}>İptal</button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? "Yükleniyor..." : editingUserId ? "Güncelle" : "Oluştur"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PASSWORD CHANGE MODAL */}
      {showPasswordModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }} role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Şifre Değiştir</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closePasswordModal}
                ></button>
              </div>

              <div className="modal-body">
                <form onSubmit={handlePasswordSubmit}>
                  {/* CURRENT PASSWORD */}
                  <div className="mb-20">
                    <label className="form-label mb-8">Mevcut Şifre *</label>
                    <div className="input-group">
                      <input
                        type={showPasswords.current ? "text" : "password"}
                        name="current_password"
                        className="form-control"
                        placeholder="Mevcut şifrenizi girin"
                        value={passwordFormData.current_password}
                        onChange={handlePasswordInputChange}
                        required
                      />
                      <button
                        type="button"
                        className="input-group-text btn btn-light"
                        onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                      >
                        <Icon icon={showPasswords.current ? "mdi:eye-off" : "mdi:eye"} />
                      </button>
                    </div>
                  </div>

                  {/* NEW PASSWORD */}
                  <div className="mb-20">
                    <label className="form-label mb-8">Yeni Şifre *</label>
                    <div className="input-group">
                      <input
                        type={showPasswords.new ? "text" : "password"}
                        name="new_password"
                        className="form-control"
                        placeholder="Yeni şifre (min. 6 karakter)"
                        value={passwordFormData.new_password}
                        onChange={handlePasswordInputChange}
                        required
                      />
                      <button
                        type="button"
                        className="input-group-text btn btn-light"
                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                      >
                        <Icon icon={showPasswords.new ? "mdi:eye-off" : "mdi:eye"} />
                      </button>
                    </div>
                  </div>

                  {/* CONFIRM PASSWORD */}
                  <div className="mb-20">
                    <label className="form-label mb-8">Şifre Doğrulama *</label>
                    <div className="input-group">
                      <input
                        type={showPasswords.confirm ? "text" : "password"}
                        name="new_password_confirmation"
                        className="form-control"
                        placeholder="Yeni şifreyi tekrar girin"
                        value={passwordFormData.new_password_confirmation}
                        onChange={handlePasswordInputChange}
                        required
                      />
                      <button
                        type="button"
                        className="input-group-text btn btn-light"
                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                      >
                        <Icon icon={showPasswords.confirm ? "mdi:eye-off" : "mdi:eye"} />
                      </button>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closePasswordModal}
                      disabled={loading}
                    >
                      İptal Et
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? "Yükleniyor..." : "Şifre Değiştir"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RESET PASSWORD CONFIRMATION MODAL */}
      {showResetConfirm && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }} role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h5 className="modal-title">
                  <Icon icon="lucide:alert-circle" className="me-2" style={{ verticalAlign: "middle" }} />
                  Şifre Sıfırlama
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelResetPassword}
                  disabled={loading}
                ></button>
              </div>

              <div className="modal-body py-4">
                <div className="alert alert-info mb-0" role="alert">
                  <div className="fw-semibold mb-2">
                    ⚠️ Onay Gerekli
                  </div>
                  <p className="mb-0">
                    Bu kullanıcının şifresi sıfırlanacak ve sistem tarafından otomatik olarak oluşturulan geçici bir şifre atanacaktır. Devam etmek istediğinizden emin misiniz?
                  </p>
                </div>
              </div>

              <div className="modal-footer border-top">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelResetPassword}
                  disabled={loading}
                >
                  İptal Et
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={confirmResetPassword}
                  disabled={loading}
                >
                  {loading ? "Sıfırlanıyor..." : "Şifreyi Sıfırla"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RESET SUCCESS MODAL */}
      {showResetSuccess && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }} role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-success">
              <div className="modal-header bg-success-focus border-bottom">
                <h5 className="modal-title text-success fw-bold">
                  <Icon icon="lucide:check-circle" className="me-2" style={{ verticalAlign: "middle" }} />
                  Şifre Başarıyla Sıfırlandı
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeResetSuccess}
                ></button>
              </div>

              <div className="modal-body py-4">
                <div className="mb-3">
                  <small className="text-muted d-block mb-2">Kullanıcı E-Mail:</small>
                  <div className="font-monospace bg-light p-2 rounded">
                    {resetSuccessData.email}
                  </div>
                </div>

                <div className="mb-3">
                  <small className="text-muted d-block mb-2">Geçici Şifre (kopyalanabilir):</small>
                  <div
                    className="bg-dark p-3 rounded text-warning font-monospace text-center fw-bold"
                    style={{ userSelect: "all", cursor: "pointer", fontSize: "16px", letterSpacing: "1px" }}
                    title="Kopyalamak için tıklayın"
                    onClick={() => {
                      navigator.clipboard.writeText(resetSuccessData.newPassword);
                      toast.success("Şifre kopyalandı!");
                    }}
                  >
                    {resetSuccessData.newPassword}
                  </div>
                </div>

                {resetSuccessData.notice && (
                  <div className="alert alert-info mb-0">
                    <small className="text-muted d-block mb-2">Sistem Notu:</small>
                    <div className="text-info">
                      {resetSuccessData.notice}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer border-top">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={closeResetSuccess}
                >
                  Tamam
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* USER PROFILE MODAL */}
      {showUserProfileModal && selectedUserProfile && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }} role="dialog">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-primary-focus border-bottom">
                <h5 className="modal-title text-white fw-bold">
                  <Icon icon="lucide:user-circle" className="me-2" style={{ verticalAlign: "middle" }} />
                  Kullanıcı Profili
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={closeUserProfileModal}
                ></button>
              </div>

              <div className="modal-body">
                {/* USER HEADER */}
                <div className="row mb-4 pb-3 border-bottom">
                  <div className="col-md-8">
                    <h6 className="mb-1 fw-bold text-dark">
                      {selectedUserProfile.name} {selectedUserProfile.surname}
                    </h6>
                    <p className="text-muted small mb-2">{selectedUserProfile.email}</p>
                    <div className="d-flex gap-2">
                      {(selectedUserProfile.isActive == 1 || selectedUserProfile.isActive === true) ? (
                        <span className="badge bg-success-100 text-success-700">Aktif</span>
                      ) : (
                        <span className="badge bg-danger-100 text-danger-700">Pasif</span>
                      )}
                      {selectedUserProfile.isPremium && (
                        <span className="badge bg-warning-100 text-warning-700">Premium</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4 text-end">
                    <div className="bg-light p-3 rounded">
                      <small className="text-muted d-block">ID</small>
                      <strong className="text-dark">#{selectedUserProfile.id}</strong>
                    </div>
                  </div>
                </div>

                {/* PERSONAL INFORMATION */}
                <div className="mb-4">
                  <h6 className="fw-semibold mb-3">Kullanıcı Bilgileri</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <small className="text-muted d-block mb-1">Kullanıcı Adı</small>
                      <p className="mb-0 text-dark">{selectedUserProfile.userName || "-"}</p>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted d-block mb-1">Doğum Yılı</small>
                      <p className="mb-0 text-dark">{selectedUserProfile.birthYear || "-"}</p>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted d-block mb-1">Şehir / İlçe</small>
                      <p className="mb-0 text-dark">
                        {selectedUserProfile.cityName || selectedUserProfile.city || "-"} / {selectedUserProfile.districtName || selectedUserProfile.district || "-"}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted d-block mb-1">Eğitim Seviyesi / Sınıf</small>
                      <p className="mb-0 text-dark">
                        Seviye: {selectedUserProfile.educationLevel || "-"} / Sınıf: {selectedUserProfile.grade || "-"}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted d-block mb-1">Dil</small>
                      <p className="mb-0 text-dark">{selectedUserProfile.language || "-"}</p>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted d-block mb-1">Premium Bitiş</small>
                      <p className="mb-0 text-dark text-warning fw-bold">
                        {selectedUserProfile.premiumExpireDate ? selectedUserProfile.premiumExpireDate.substring(0, 10) : "Yok"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer border-top">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeUserProfileModal}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* PREMIUM MODAL */}
      {showPremiumModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }} role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isBulkPremium ? `Toplu Premium Güncelle (${premiumData.userIds.length} Kullanıcı)` : "Premium Durumu Güncelle"}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowPremiumModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handlePremiumSubmit}>
                  <div className="mb-20">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={premiumData.isPremium}
                        onChange={(e) => setPremiumData({ ...premiumData, isPremium: e.target.checked })}
                        id="isPremiumSwitch"
                      />
                      <label className="form-check-label" htmlFor="isPremiumSwitch">Premium Aktif</label>
                    </div>
                  </div>
                  <div className="mb-20">
                    <label className="form-label">Premium Bitiş Tarihi</label>
                    <input
                      type="date"
                      className="form-control"
                      value={premiumData.premiumExpireDate}
                      onChange={(e) => setPremiumData({ ...premiumData, premiumExpireDate: e.target.value })}
                      required
                    />
                  </div>
                  <div className="modal-footer px-0 pb-0">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowPremiumModal(false)} disabled={loading}>İptal</button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? "Guncelleniyor..." : "Kaydet"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;

